import {
  HTTP_INTERCEPTORS,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Provider } from '@angular/core';
import { Router } from '@angular/router';
import { SHOULD_ADD_AUTHENTICATION_HEADER } from '@elio/emanager-front/auth/model';
import { UserSelectors } from '@elio/emanager-front/auth/state';
import { ElioTypedRouter } from '@elio/emanager-front/shared/tools/router';
import { BACKEND_URL } from '@elio/shared/models';
import { localStorageRemove, sessionStorageRemove } from '@elio/shared/utils';
import { Store } from '@ngrx/store';
import { Observable, catchError, first, mergeMap, throwError } from 'rxjs';
import { JwtTokenService } from './jwt-token.service';
import { JwtTokenUtils } from './jwt-token.utils';

class JwtTokenInterceptor implements HttpInterceptor {
  constructor(
    private store: Store,
    private router: ElioTypedRouter,
    private jwtTokenService: JwtTokenService
  ) {}

  private readonly NO_REFRESH_CODES = ['JWT_REFRESH_TOKEN_EXPIRED', 'JWT_REFRESH_TOKEN_INVALID', 'INVALID_CREDENTIALS'];

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return this.store.select(UserSelectors.getJwtAccessToken).pipe(
      first(),
      mergeMap((jwtToken) => {
        const isApiUrl = request.url.includes(BACKEND_URL) && !request.url.includes(BACKEND_URL + '/auth');
        if (isApiUrl && request.context.get(SHOULD_ADD_AUTHENTICATION_HEADER)) {
          request = JwtTokenUtils.addJwtAuthorizationHeader(request, jwtToken);
        }
        return next.handle(request).pipe(
          catchError((err: unknown) => {
            if (err instanceof HttpErrorResponse && err.status === 401) {
              const errorCode = err.error?.code;
              const isNoRefreshError = this.NO_REFRESH_CODES.includes(errorCode);
              if (!err.error || !isNoRefreshError) {
                return this.jwtTokenService.retryWithRefreshJwtToken(request, next);
              }
              localStorageRemove('jwtAccessToken');
              sessionStorageRemove('jwtAccessToken');
              this.router.navigate(['login']);
            }
            return throwError(() => err);
          })
        );
      })
    );
  }
}

export const JWT_TOKEN_INTERCEPTOR_PROVIDER: Provider = {
  provide: HTTP_INTERCEPTORS,
  useClass: JwtTokenInterceptor,
  multi: true,
  deps: [Store, Router, JwtTokenService],
};
